import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { addDays, fmtHeure, fmtNum, fmtPct, todayIso } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiTile } from "@/components/common/KpiTile";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { LignesComparaison } from "@/components/trs/LignesComparaison";
import { ParQuartDetail } from "@/components/trs/ParQuartDetail";
import { MatriceLignesQuarts } from "@/components/trs/MatriceLignesQuarts";
import { PeriodeComparaison } from "@/components/trs/PeriodeComparaison";
import { TrendChart } from "@/components/dashboard/TrendChart";

const REFRESH_MS = 10000;

export default function TrsPage() {
  const { entrepriseId, user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const ligneId = Number(sp.get("ligne_id")) || null;
  const dateJour = sp.get("date") || todayIso();
  const setParam = (k, v) => setSp((p) => { const n = new URLSearchParams(p); v ? n.set(k, v) : n.delete(k); return n; }, { replace: true });
  const ready = user.role !== "superadmin" || !!entrepriseId;

  const dash = useQuery({ queryKey: ["dashboard", todayIso(), entrepriseId], queryFn: () => get("/dashboard", { date_jour: todayIso() }), refetchInterval: REFRESH_MS, enabled: ready });
  const lignes = dash.data?.lignes || [];
  const actives = lignes.filter((l) => l.statut !== "hors_service");
  useEffect(() => { if (!ligneId && actives.length) setParam("ligne_id", String(actives[0].ligne_id)); }, [actives, ligneId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = lignes.find((l) => l.ligne_id === ligneId);
  const avecTrs = actives.filter((l) => l.trs != null);
  const meilleure = avecTrs.reduce((a, l) => (!a || l.trs > a.trs ? l : a), null);
  const pire = avecTrs.reduce((a, l) => (!a || l.trs < a.trs ? l : a), null);
  const enArret = actives.filter((l) => l.arret_categorie && !l.arret_planifie).length;

  return (
    <div data-testid="trs-page" className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Pilotage" }, { label: "TRS / OEE" }]} title="TRS / OEE par ligne et par quart"
        subtitle={dash.data ? `Temps réel · rafraîchi toutes les ${REFRESH_MS / 1000} s · dernière mise à jour ${fmtHeure(new Date(dash.dataUpdatedAt).toISOString())}` : "Chargement…"}
        actions={<>
          <select data-testid="trs-ligne-select" value={ligneId || ""} onChange={(e) => setParam("ligne_id", e.target.value)} className="h-7 bg-input border border-border rounded-sm text-[11px] px-1.5 max-w-[180px]">
            {lignes.map((l) => <option key={l.ligne_id} value={l.ligne_id}>{l.ligne}</option>)}
          </select>
          <div className="flex items-center gap-1 text-[11px]">
            <button data-testid="trs-date-prev" className="h-7 w-7 border border-border rounded-sm hover:bg-muted font-mono" onClick={() => setParam("date", addDays(dateJour, -1))}>‹</button>
            <input data-testid="trs-date-input" type="date" value={dateJour} max={todayIso()} onChange={(e) => e.target.value && setParam("date", e.target.value)} className="h-7 bg-input border border-border rounded-sm px-2 font-mono text-[11px]" />
            <button data-testid="trs-date-next" className="h-7 w-7 border border-border rounded-sm hover:bg-muted font-mono disabled:opacity-40" disabled={dateJour >= todayIso()} onClick={() => setParam("date", addDays(dateJour, 1))}>›</button>
          </div>
          <button data-testid="trs-refresh" onClick={() => dash.refetch()} className="h-7 px-2 border border-border rounded-sm text-[11px] flex items-center gap-1 hover:bg-muted"><RefreshCw className={cn("h-3 w-3", dash.isFetching && "animate-spin")} />Actualiser</button>
        </>} />

      {!ready ? <EmptyState title="Sélectionnez une entreprise" hint="En tant que superadmin, choisissez l'entreprise à analyser dans la barre supérieure." />
        : dash.isLoading ? <LoadingState rows={6} /> : dash.error ? <ErrorState error={dash.error} onRetry={dash.refetch} /> : (<>
        <div data-testid="trs-kpis" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
          <KpiTile testId="kpi-trs-global" label="TRS ligne — aujourd'hui" value={fmtPct(dash.data.resume?.trs_global)} trs={dash.data.resume?.trs_global} sub="Toutes lignes · journée industrielle" />
          <KpiTile testId="kpi-lignes-actives" label="Lignes actives" value={actives.length} sub={`${lignes.length - actives.length} hors service`} />
          <KpiTile testId="kpi-lignes-marche" label="En marche" value={actives.filter((l) => l.statut === "marche").length} tone="ok" sub={`${enArret} en arrêt non planifié`} />
          <KpiTile testId="kpi-meilleure" label="Meilleure ligne" value={meilleure ? fmtPct(meilleure.trs) : "—"} trs={meilleure?.trs} sub={meilleure?.ligne} />
          <KpiTile testId="kpi-pire" label="Ligne la plus faible" value={pire ? fmtPct(pire.trs) : "—"} trs={pire?.trs} sub={pire?.ligne} />
          <KpiTile testId="kpi-ecart" label="Écart max" value={meilleure && pire ? fmtNum(meilleure.trs - pire.trs, 1) : "—"} unit="pts" sub="entre lignes" />
        </div>

        <LignesComparaison lignes={lignes} selected={ligneId} onSelect={(id) => setParam("ligne_id", String(id))} />

        <MatriceLignesQuarts lignes={actives} dateJour={dateJour} refreshMs={REFRESH_MS} selected={ligneId} onSelect={(id) => setParam("ligne_id", String(id))} />

        {selected && (
          <div data-testid="trs-diagnostic" className="panel px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border-l-2 border-l-primary">
            <span className="eyebrow">Diagnostic · {selected.ligne}</span>
            <span className="font-medium">{selected.diagnostic}</span>
          </div>
        )}

        <ParQuartDetail ligneId={ligneId} dateJour={dateJour} refreshMs={REFRESH_MS} />

        <TrendChart ligneId={ligneId} key={ligneId} />

        <PeriodeComparaison lignes={actives} />
      </>)}
    </div>
  );
}
